import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { NGXLogger } from 'ngx-logger';
import { MatDialog } from '@angular/material/dialog';
import { UsersService } from '../../users/users.service';
import { CigpStaffService } from '../../cigp-staff/cigp-staff.service';
import { AuditTrailUserActionsItem, EnrichedAuditTrailUserActionsItem } from './audit-trail-user-actions-item.model';
import { catchError, combineLatest, map, Observable, OperatorFunction, throwError } from 'rxjs';
import { showErrorDialog } from '../../shared/utils';
import { User } from '../../users/user.model';
import { CigpStaff } from '../../cigp-staff/cigp-staff.model';

function enrichAuditTrailUserActionItem(
  item: AuditTrailUserActionsItem,
  users: User[],
  staffs: CigpStaff[]
): EnrichedAuditTrailUserActionsItem {
  const enriched = {
    ...item,
    client: users.find((u) => u.email === item.clientEmail) || null,
    submitter: staffs.find((s) => s.email === item.submitterEmail) || null
  };

  return enriched;
}

function enrichObservableAuditTrailUserActionsItems$(
  usersService: UsersService,
  cigpStaffService: CigpStaffService
): OperatorFunction<AuditTrailUserActionsItem[], EnrichedAuditTrailUserActionsItem[]> {
  return (source: Observable<AuditTrailUserActionsItem[]>) =>
    combineLatest([source, usersService.getUsersFromCache$(), cigpStaffService.getStaffsFromCache$()]).pipe(
      map(([items, users, staffs]) => items.map((item) => enrichAuditTrailUserActionItem(item, users, staffs))),
      map((items) => items.filter((r) => r !== null) as EnrichedAuditTrailUserActionsItem[])
    );
}

@Injectable({
  providedIn: 'root'
})
export class AuditTrailUserActionsService {
  private apiEndpoint = `${environment.apiEndpoint}/api/audit-trail/user-actions`;

  constructor(
    private http: HttpClient,
    private logger: NGXLogger,
    private errorDialog: MatDialog,
    private usersService: UsersService,
    private cigpStaffService: CigpStaffService
  ) {}

  public getAuditTrailUserActionsItems$(
    startDate: Date,
    endDate: Date
  ): Observable<EnrichedAuditTrailUserActionsItem[]> {
    return this.http
      .get<AuditTrailUserActionsItem[]>(`${this.apiEndpoint}`, {
        params: this.computeQueryParams(startDate, endDate)
      })
      .pipe(
        catchError((error: any) => {
          this.logger.error(error);
          showErrorDialog(this.errorDialog, error);
          return throwError(() => error);
        }),
        enrichObservableAuditTrailUserActionsItems$(this.usersService, this.cigpStaffService)
      );
  }

  private computeQueryParams(startDate: Date, endDate: Date) {
    const params: { [param: string]: string } = {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    };

    return params;
  }
}
